import { IEventDefinition } from "@highway/mbs-analytics";

export const appEvents: Record<string, IEventDefinition> = {
    // Highway Record Events
    highway_record_video_play_click: {
        description: "Fires when a user clicks to play a saved recording",
        mappings: {
            mixpanel: "highway_record_video_play_click",
        },
        validTools: ["record"],
    },
    highway_record_video_rename_click: {
        description: "Fires when a user clicks to rename a recording",
        mappings: {
            mixpanel: "highway_record_video_rename_click",
        },
        validTools: ["record"],
    },
    highway_record_video_download_click: {
        description: "Fires when a user clicks to download a recording",
        mappings: {
            mixpanel: "highway_record_video_download_click",
        },
        validTools: ["record"],
    },
    highway_record_video_send_click: {
        description: "Fires when a user clicks to send a recording",
        mappings: {
            mixpanel: "highway_record_video_send_click",
        },
        validTools: ["record"],
    },
    highway_record_video_delete_click: {
        description: "Fires when a user deletes a saved recording",
        mappings: {
            mixpanel: "highway_record_video_delete_click",
        },
        validTools: ["record"],
    },
    highway_record_new_click: {
        description: "Fires when the user clicks 'Record New Video'",
        mappings: {
            mixpanel: "highway_record_new_click",
        },
    },
    highway_record_settings_selected: {
        description: "Fires when the user selects webcam/screen options before starting",
        mappings: {
            mixpanel: "highway_record_settings_selected",
        },
        validTools: ["record"],
    },
    highway_record_start_click: {
        description: "Fires when the user clicks 'Start Recording'",
        mappings: {
            mixpanel: "highway_record_start_click",
        },
        validTools: ["record"],
    },
    // Highway Meeting Events
    highway_meeting_host_click: {
        description: "Fires when the user clicks 'Host a Meeting' to open meeting setup",
        mappings: {
            mixpanel: "highway_meeting_host_click",
        },
    },
    highway_meeting_settings_selected: {
        description: "Fires when the user chooses video/screen options before launching the meeting",
        mappings: {
            mixpanel: "highway_meeting_settings_selected",
        },
    },
    highway_meeting_window_opened: {
        description: "Fires when the new browser tab/window opens for the live meeting room",
        mappings: {
            mixpanel: "highway_meeting_window_opened",
        },
        validTools: ["meetings"],
    },
    highway_meeting_invite_modal_view: {
        description: "Fires when the client invite modal is shown",
        mappings: {
            mixpanel: "highway_meeting_invite_modal_view",
        },
        validTools: ["meetings"],
    },
    highway_meeting_invite_send_click: {
        description: "Fires when the user clicks 'Send' to invite a client",
        mappings: {
            mixpanel: "highway_meeting_invite_send_click",
        },
        validTools: ["meetings"],
    },
    highway_meeting_invite_send_success: {
        description: "Fires when the invite email is successfully sent",
        mappings: {
            mixpanel: "highway_meeting_invite_send_success",
        },
    },
    highway_meeting_passcode_entered: {
        description: "Fires when the client enters the passcode to join the meeting",
        mappings: {
            mixpanel: "highway_meeting_passcode_entered",
        },
        validTools: ["meetings"],
    },
    highway_meeting_join_success: {
        description: "Fires when the client successfully joins the live meeting room",
        mappings: {
            mixpanel: "highway_meeting_join_success",
        },
        validTools: ["meetings"],
    },
    highway_meeting_end_click: {
        description: "Fires when the host clicks 'End Meeting' in the meeting room",
        mappings: {
            mixpanel: "highway_meeting_end_click",
        },
        validTools: ["meetings"],
    },
    highway_meeting_end_confirm_modal_view: {
        description: "Fires when the confirmation modal appears",
        mappings: {
            mixpanel: "highway_meeting_end_confirm_modal_view",
        },
        validTools: ["meetings"],
    },
    highway_meeting_end_confirm_click: {
        description: "Fires when the user confirms and ends the meeting",
        mappings: {
            mixpanel: "highway_meeting_end_confirm_click",
        },
        validTools: ["meetings"],
    },
    highway_meeting_end_success: {
        description: "Fires when the meeting is successfully closed",
        mappings: {
            mixpanel: "highway_meeting_end_success",
        },
        validTools: ["meetings"],
    },
    // Home page events
    home_top_banner_click: {
        description: "Fires when a user clicks the top banner.",
        mappings: {
            mixpanel: "home_top_banner_click",
        },
    },
    home_morning_update_click: {
        description: "Fires when a user clicks on the Morning Update section.",
        mappings: {
            mixpanel: "home_morning_update_click",
        },
    },
    home_market_overview_card_click: {
        description: "Fires when a user clicks on a Market Overview card.",
        mappings: {
            mixpanel: "home_market_overview_card_click",
        },
    },
    home_featured_widget_click: {
        description: "Fires when a user clicks on a featured widget.",
        mappings: {
            mixpanel: "home_featured_widget_click",
        },
    },
    home_resource_click: {
        description: "Fires when a user clicks on a resource link in the Resources section.",
        mappings: {
            mixpanel: "home_resource_click",
        },
    },
    home_report_widget_view: {
        description: "Fires when widget is displayed for an eligible user.",
        mappings: {
            mixpanel: "home_report_widget_view",
        },
    },
    home_report_widget_banner_click: {
        description: "Fires when user clicks anywhere on the banner area (excluding transaction CTAs).",
        mappings: {
            mixpanel: "home_report_widget_banner_click",
        },
    },
    home_report_transaction_cta_click: {
        description: "Fires when user clicks the “View” CTA for a specific transaction.",
        mappings: {
            mixpanel: "home_report_transaction_cta_click",
        },
    },
    // Newsletter events
    weekly_newsletter_copy_all_click: {
        description:
            "Fires when a user clicks 'Copy All' to copy the newsletter content. Properties: newsletter (date of the newsletter)",
        mappings: {
            mixpanel: "weekly_newsletter_copy_all_click",
        },
    },
    weekly_newsletter_preview_link_click: {
        description:
            "Fires when a user clicks 'Preview Link' to open the live digital view. Properties: newsletter (date of the newsletter)",
        mappings: {
            mixpanel: "weekly_newsletter_preview_link_click",
        },
    },
    // Risk of Waiting events
    row_calculate_click: {
        description: "Fires when the user clicks the 'Calculate' button after entering inputs.",
        mappings: {
            mixpanel: "row_calculate_click",
        },
    },
    row_calculate_success: {
        description: "Fires when the report successfully generates.",
        mappings: {
            mixpanel: "row_calculate_success",
        },
    },
    row_edit_click: {
        description: "Fires when the user clicks 'Edit' to open the edit drawer.",
        mappings: {
            mixpanel: "row_edit_click",
        },
    },
    row_edit_update_click: {
        description: "Fires when the user clicks 'Update' after editing input fields.",
        mappings: {
            mixpanel: "row_edit_update_click",
        },
    },
    row_edit_update_success: {
        description: "Fires when the updated report is successfully reflected.",
        mappings: {
            mixpanel: "row_edit_update_success",
        },
    },
    // Payment Calculator events
    payment_calc_calculate_click: {
        description: "Fires when the user clicks 'View Schedule' after entering inputs.",
        mappings: {
            mixpanel: "payment_calc_calculate_click",
        },
    },
    payment_calc_calculate_success: {
        description: "Fires when the report is successfully generated.",
        mappings: {
            mixpanel: "payment_calc_calculate_success",
        },
    },
    blended_rate_add_debt_click: {
        description: 'Fires when the user clicks "Add Debt" to include additional debts.',
        mappings: {
            mixpanel: "blended_rate_add_debt_click",
        },
    },
    blended_rate_reset_click: {
        description: 'Fires when the user clicks "Reset" to clear inputs.',
        mappings: {
            mixpanel: "blended_rate_reset_click",
        },
    },
    reinvestment_calc_calculate_click: {
        description: 'Fires when the user clicks "Calculate".',
        mappings: {
            mixpanel: "reinvestment_calc_calculate_click",
        },
    },
    reinvestment_calc_calculate_success: {
        description: "Fires after values are valid and saves.",
        mappings: {
            mixpanel: "reinvestment_calc_calculate_success",
        },
    },
    reinvestment_calc_edit_click: {
        description: 'Fires when the user clicks "Edit".',
        mappings: {
            mixpanel: "reinvestment_calc_edit_click",
        },
    },
    // Affordability Calculator events
    affordability_calc_page_view: {
        description: "Fires when the user views the affordability calculator page.",
        mappings: {
            mixpanel: "affordability_calc_page_view",
        },
        validTools: ["aff"],
    },
    affordability_calc_mode_change: {
        description: "Fires when the user toggles between income and payment mode.",
        mappings: {
            mixpanel: "affordability_calc_mode_change",
        },
        validTools: ["aff"],
    },
    affordability_calc_reset_click: {
        description: "Fires when the user clicks the reset calculator button.",
        mappings: {
            mixpanel: "affordability_calc_reset_click",
        },
        validTools: ["aff"],
    },
    affordability_calc_reset_confirm: {
        description: "Fires when the user confirms the reset action.",
        mappings: {
            mixpanel: "affordability_calc_reset_confirm",
        },
        validTools: ["aff"],
    },
    // ARM vs Fixed events
    arm_vs_fixed_calculate_click: {
        description: "Fires when the user clicks the 'Calculate' button after entering inputs.",
        mappings: {
            mixpanel: "arm_vs_fixed_calculate_click",
        },
    },
    arm_vs_fixed_calculate_success: {
        description: "Fires when the report successfully generates.",
        mappings: {
            mixpanel: "arm_vs_fixed_calculate_success",
        },
    },
};
